import React, { useState, useEffect } from 'react';
import { Check, X, Loader, Plus } from 'lucide-react';
import { apiService, CustomFieldDefinition } from '../services/api';
import './NotionFieldMapping.css';

interface FieldMapping {
  [notionColumnName: string]: {
    mantisField: string;
    type: 'standard' | 'custom';
    customFieldId?: string;
    notionType: string;
  };
}

interface NotionFieldMappingProps {
  mapping: FieldMapping;
  notionColumns: string[];
  entryCount: number;
  onConfirm: (mapping: FieldMapping) => Promise<void>;
  onCancel: () => void;
}

const STANDARD_FIELDS = [
  { value: 'name', label: 'Name' },
  { value: 'company', label: 'Company' },
  { value: 'email', label: 'Email' },
  { value: 'roleTitle', label: 'Role/Title' },
  { value: 'stage', label: 'Stage' },
  { value: 'industry', label: 'Industry' },
  { value: 'location', label: 'Location' },
  { value: 'status', label: 'Status' },
  { value: 'owner', label: 'Owner' },
  { value: 'tag', label: 'Tag' },
  { value: 'notes', label: 'Notes/Latest Note' },
  { value: 'profileUrl', label: 'Profile URL' },
  { value: 'xUrl', label: 'X URL' },
  { value: 'substackUrl', label: 'Substack URL' },
  { value: 'accelerator', label: 'Accelerator' },
  { value: 'contactPhone', label: 'Contact Phone' },
  { value: 'lastEditedBy', label: 'Last Edited By' },
  { value: 'type', label: 'Type' },
  { value: 'vcFirm', label: 'VC Firm' },
];

export const NotionFieldMapping: React.FC<NotionFieldMappingProps> = ({
  mapping: initialMapping,
  notionColumns,
  entryCount,
  onConfirm,
  onCancel,
}) => {
  const [mapping, setMapping] = useState<FieldMapping>(initialMapping);
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCustomFields();
  }, []);

  const loadCustomFields = async () => {
    try {
      const fields = await apiService.getCustomFields();
      setCustomFields(fields);
    } catch (err: any) {
      console.error('Error loading custom fields:', err);
    }
  };

  const handleMappingChange = (notionColumn: string, mantisField: string, type: 'standard' | 'custom', customFieldId?: string) => {
    setMapping((prev) => ({
      ...prev,
      [notionColumn]: {
        mantisField,
        type,
        customFieldId,
        notionType: prev[notionColumn]?.notionType || 'rich_text',
      },
    }));
  };

  const handleCreateCustomField = async (notionColumn: string) => {
    try {
      setLoading(true);
      const notionType = mapping[notionColumn]?.notionType || 'rich_text';
      
      // Map Notion type to Remi AI type
      const typeMap: { [key: string]: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean' | 'url' | 'email' | 'phone' | 'person' } = {
        select: 'select',
        multi_select: 'multiselect',
        number: 'number',
        date: 'date',
        checkbox: 'boolean',
        rich_text: 'text',
        title: 'text',
        email: 'email',
        phone_number: 'phone',
        url: 'url',
        status: 'select',
        people: 'person',
      };

      const fieldType = typeMap[notionType] || 'text';

      const newField = await apiService.createCustomField({
        name: notionColumn.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        label: notionColumn,
        type: fieldType,
        options: [],
      });

      setCustomFields((prev) => [...prev, newField]);
      
      // Update mapping to use the new custom field
      handleMappingChange(notionColumn, `custom:${newField.id}`, 'custom', newField.id);
    } catch (err: any) {
      setError(err.message || 'Failed to create custom field');
      console.error('Error creating custom field:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setSaving(true);
      setError(null);

      // Transform mapping to format expected by backend
      const transformedMapping: FieldMapping = {};
      for (const [notionCol, mapInfo] of Object.entries(mapping)) {
        if (mapInfo.mantisField && mapInfo.mantisField !== 'skip') {
          transformedMapping[notionCol] = {
            mantisField: mapInfo.type === 'custom' && mapInfo.customFieldId 
              ? `custom:${mapInfo.customFieldId}` 
              : mapInfo.mantisField,
            type: mapInfo.type,
            customFieldId: mapInfo.customFieldId,
            notionType: mapInfo.notionType,
          };
        }
      }

      await onConfirm(transformedMapping);
    } catch (err: any) {
      setError(err.message || 'Failed to confirm mapping');
      console.error('Error confirming mapping:', err);
    } finally {
      setSaving(false);
    }
  };

  const getMantisFieldLabel = (mantisField: string, type: 'standard' | 'custom'): string => {
    if (type === 'standard') {
      const field = STANDARD_FIELDS.find((f) => f.value === mantisField);
      return field?.label || mantisField;
    } else {
      const customField = customFields.find((f) => f.id === mantisField.replace('custom:', ''));
      return customField?.label || mantisField;
    }
  };

  return (
    <div className="notion-field-mapping">
      <div className="mapping-header">
        <h2>Map Notion Columns to Remi AI Fields</h2>
        <p className="mapping-subtitle">
          We found {entryCount} entries in your Notion database. Map each column to the corresponding Remi AI field.
        </p>
      </div>

      {error && (
        <div className="mapping-error">
          <X size={16} />
          {error}
        </div>
      )}

      <div className="mapping-table">
        <div className="mapping-table-header">
          <div className="mapping-col notion-col">Notion Column</div>
          <div className="mapping-col mantis-col">Remi Field</div>
        </div>

        <div className="mapping-table-body">
          {notionColumns.map((notionColumn) => {
            const mapInfo = mapping[notionColumn];
            const currentMantisField = mapInfo?.mantisField || '';
            const currentType = mapInfo?.type || 'standard';

            return (
              <div key={notionColumn} className="mapping-row">
                <div className="mapping-col notion-col">
                  <div className="notion-column-name">
                    {notionColumn}
                  </div>
                </div>

                <div className="mapping-col mantis-col">
                  <select
                    value={currentType === 'custom' && mapInfo?.customFieldId
                      ? `custom:${mapInfo.customFieldId}`
                      : currentMantisField || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'skip') {
                        handleMappingChange(notionColumn, 'skip', 'standard');
                      } else if (value.startsWith('custom:')) {
                        const customFieldId = value.replace('custom:', '');
                        handleMappingChange(notionColumn, value, 'custom', customFieldId);
                      } else if (value.startsWith('create:')) {
                        handleCreateCustomField(notionColumn);
                        // Reset select to empty while creating
                        e.target.value = '';
                      } else if (value) {
                        handleMappingChange(notionColumn, value, 'standard');
                      }
                    }}
                    disabled={loading || saving}
                  >
                    <option value="">Select field...</option>
                    <optgroup label="Standard Fields">
                      {STANDARD_FIELDS.map((field) => (
                        <option key={field.value} value={field.value}>
                          {field.label}
                        </option>
                      ))}
                    </optgroup>
                    {customFields.length > 0 && (
                      <optgroup label="Custom Fields">
                        {customFields.map((field) => (
                          <option key={field.id} value={`custom:${field.id}`}>
                            {field.label}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <option value="create:new">+ Create New Custom Field</option>
                    <option value="skip">Skip this column</option>
                  </select>
                </div>

                <div className="mapping-col action-col">
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mapping-actions">
        <button
          type="button"
          onClick={onCancel}
          className="mapping-btn mapping-btn-secondary"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          className="mapping-btn mapping-btn-primary"
          disabled={saving || loading}
        >
          {saving ? (
            <>
              <Loader size={16} className="spinner" />
              Importing...
            </>
          ) : (
            <>
              Confirm & Import {entryCount} {entryCount === 1 ? 'Entry' : 'Entries'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default NotionFieldMapping;

